// @flow
import React from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { Toast } from '../components/elements';
import { toastActions } from '../redux/modules/context/toasts';
import type { ToastData, Dispatch } from '../types';

// How long a closed toast stays mounted for its exit animation (Toast.scss)
export const TOAST_EXIT_MS = 200;

// Another dispatch of the toast on screen, e.g. one error per failed request while offline
export const isSameToast = (a: ?ToastData, b: ?ToastData): boolean => Boolean(a && b)
  && ['type', 'text', 'i18nText', 'fileURL', 'offline'].every((key) => get(a, key) === get(b, key));

type Props = {
  active: boolean,
  toastData: ?ToastData,
  dispatch: Function,
};

type State = {
  data: ?ToastData,
  // a new key for every new toast: it mounts fresh, with its own timer and entrance
  key: number,
  leaving: boolean,
};

// Always mounted (AppLayout), so a toast can animate out after the store closes it
class ToastContainer extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { data: null, key: 0, leaving: false };
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const { active, toastData } = props;
    if (active && toastData) {
      if (toastData === state.data && !state.leaving) return null;
      // The same toast again stays where it is (no new entrance or announcement); Toast restarts its timer
      const same = !state.leaving && isSameToast(state.data, toastData);
      return { data: toastData, key: same ? state.key : state.key + 1, leaving: false };
    }
    if (state.data && !state.leaving) return { leaving: true };
    return null;
  }

  componentDidUpdate() {
    if (this.state.leaving && !this.exitTimeoutId) {
      this.exitTimeoutId = setTimeout(this.handleExited, TOAST_EXIT_MS);
    } else if (!this.state.leaving && this.exitTimeoutId) {
      clearTimeout(this.exitTimeoutId);
      this.exitTimeoutId = null;
    }
  }

  componentWillUnmount() {
    if (this.exitTimeoutId) clearTimeout(this.exitTimeoutId);
  }

  render() {
    const { data, key, leaving } = this.state;
    if (!data) return null;

    return (
      <Toast
        key={key}
        toastData={data}
        leaving={leaving}
        onClose={this.closeToast}
      />
    );
  }

  exitTimeoutId: any = null;

  handleExited = () => {
    this.exitTimeoutId = null;
    if (this.state.leaving) this.setState({ data: null, leaving: false });
  };

  closeToast = () => {
    this.props.dispatch(toastActions.closeActiveToast());
  };
}

const mapStateToProps = (state) => ({
  active: Boolean(get(state.context, ['toasts', 'active'])),
  toastData: get(state.context, ['toasts', 'data'], null),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({ dispatch });

export default connect<any, any, any, any, any, any>(
  mapStateToProps,
  mapDispatchToProps,
)(ToastContainer);
